import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { api } from "./api.ts";
import { parse, stringify, statusOf, withStatus } from "./frontmatter.ts";
import type { Status } from "./types.ts";

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "published", label: "已發表" },
  { value: "draft", label: "草稿" },
  { value: "unlisted", label: "未列出" },
  { value: "archived", label: "封存" },
];

export function Editor(props: {
  path: string;
  initialStatus: Status;
  allTags: string[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: (path: string, sha: string, status: Status) => void;
}) {
  const { path } = props;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null); // 訪客動作被攔下的提示，非錯誤
  const [sha, setSha] = useState<string | undefined>();
  const [data, setData] = useState<Record<string, any>>({});
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<Status>(props.initialStatus);
  const [preview, setPreview] = useState(true); // default to preview
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const f = await api.file(path);
        const p = parse(f.content);
        setSha(f.sha);
        setData(p.data);
        setBody(p.body);
        setStatus(statusOf(p.data));
      } catch (e) {
        setError(String((e as Error).message));
      } finally {
        setLoading(false);
      }
    })();
  }, [path]);

  function markDirty() {
    setDirty(true);
    setSavedAt(null);
  }

  function setField(key: string, value: any) {
    setData((d) => ({ ...d, [key]: value }));
    markDirty();
  }

  // Save with whatever status the dropdown currently holds — one button, no publish/draft split.
  // 訪客也能操作整個編輯器；這裡才是唯一攔下「不送出 commit」的地方。
  async function save() {
    if (!props.canEdit) {
      setNotice("訪客唯讀模式——變更不會送出，登入後即可儲存。");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const finalData = withStatus(data, status);
      const content = stringify(finalData, body);
      const res = await api.save({
        path,
        content,
        sha,
        message: `content: ${status === "published" ? "publish" : "update"} ${path}`,
      });
      setSha(res.sha);
      setData(finalData);
      setDirty(false);
      setSavedAt(new Date().toLocaleTimeString());
      props.onSaved(path, res.sha, status);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  }

  // ---- markdown editing helpers ----
  function replaceRange(next: string, selStart: number, selEnd: number) {
    setBody(next);
    markDirty();
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (!el) return;
      el.focus();
      el.selectionStart = selStart;
      el.selectionEnd = selEnd;
    });
  }

  function surround(before: string, after = before) {
    const el = bodyRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = body.slice(s, e);
    const next = body.slice(0, s) + before + sel + after + body.slice(e);
    if (sel) replaceRange(next, s + before.length, e + before.length);
    else replaceRange(next, s + before.length, s + before.length);
  }

  function prefixLines(prefix: string) {
    const el = bodyRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const lineStart = body.lastIndexOf("\n", s - 1) + 1;
    const seg = body.slice(lineStart, e);
    const replaced = seg.split("\n").map((l) => prefix + l).join("\n");
    const next = body.slice(0, lineStart) + replaced + body.slice(e);
    replaceRange(next, lineStart, lineStart + replaced.length);
  }

  function insertAtCursor(text: string) {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + text);
      markDirty();
      return;
    }
    const s = el.selectionStart, e = el.selectionEnd;
    replaceRange(body.slice(0, s) + text + body.slice(e), s + text.length, s + text.length);
  }

  async function onPickImage(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!props.canEdit) {
      setNotice("訪客唯讀模式——圖片不會上傳，登入後即可插入圖片。");
      return;
    }
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const up = await api.upload(file.name, b64);
      insertAtCursor(`\n![${file.name}](${up.url})\n`);
    } catch (err) {
      setError("圖片上傳失敗：" + String((err as Error).message));
    }
  }

  const currentTags = Array.isArray(data.tags) ? data.tags.map(String) : [];
  const tagsValue = currentTags.join(", ");
  const statusLabel = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? "";

  function toggleTag(t: string) {
    setData((d) => {
      const cur = Array.isArray(d.tags) ? d.tags.map(String) : [];
      const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
      return { ...d, tags: next };
    });
    markDirty();
  }

  const FMT: { label: React.ReactNode; title: string; run: () => void }[] = [
    { label: <b>B</b>, title: "粗體", run: () => surround("**") },
    { label: <i>I</i>, title: "斜體", run: () => surround("*") },
    { label: "H", title: "小標題", run: () => prefixLines("## ") },
    { label: "≣", title: "清單", run: () => prefixLines("- ") },
    { label: "❝", title: "引言", run: () => prefixLines("> ") },
    { label: "</>", title: "程式碼", run: () => surround("`") },
    { label: "🔗", title: "連結", run: () => surround("[", "](https://)") },
  ];

  return (
    <div className="editor">
      <header className="topbar">
        <button className="btn ghost" onClick={props.onClose}>
          ‹ 返回
        </button>
        <div className="grow">
          <div className="path" title={path}>{path}</div>
          {savedAt ? <div className="saved">已存 {savedAt}</div> : dirty ? <div className="unsaved">未儲存</div> : null}
        </div>
        {!props.canEdit && <span className="role-tag">訪客・唯讀</span>}
        <span className={`status-pill ${status}`}>{statusLabel}</span>
      </header>

      {error && <div className="banner error">{error}</div>}
      {notice && <div className="banner notice">{notice}</div>}

      {loading ? (
        <div className="muted pad">載入中…</div>
      ) : (
        <div className="editor-body">
          <div className="meta">
            <label className="field">
              <span>標題</span>
              <input
                value={data.title ?? ""}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="（此檔沒有 title 欄位，留空則不新增）"
              />
            </label>

            <div className="field-row">
              <label className="field">
                <span>狀態</span>
                <select value={status} onChange={(e) => { setStatus(e.target.value as Status); markDirty(); }}>
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>標籤</span>
                <input
                  value={tagsValue}
                  onChange={(e) => {
                    const parts = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                    setField("tags", parts);
                  }}
                  placeholder="逗號分隔，或點下方選"
                />
              </label>
            </div>

            {props.allTags.length > 0 && (
              <div className="tagcloud">
                <span className="tagcloud-label">常用標籤</span>
                {props.allTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`tagchip ${currentTags.includes(t) ? "on" : ""}`}
                    onClick={() => toggleTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="toolbar">
            <div className="seg">
              <button className={preview ? "" : "on"} onClick={() => setPreview(false)}>編輯</button>
              <button className={preview ? "on" : ""} onClick={() => setPreview(true)}>預覽</button>
            </div>
            <span className="charcount">{body.length} 字</span>
          </div>

          {!preview && (
            <div className="fmtbar">
              {FMT.map((f, i) => (
                <button key={i} className="fmt" title={f.title} onClick={f.run}>{f.label}</button>
              ))}
              <label className="fmt file" title="插入圖片">
                🖼
                <input type="file" accept="image/*" onChange={onPickImage} hidden />
              </label>
            </div>
          )}

          {preview ? (
            <div
              className="preview markdown"
              dangerouslySetInnerHTML={{ __html: marked.parse(body || "*（還沒有內容，切到「編輯」開始寫）*") as string }}
            />
          ) : (
            <textarea
              ref={bodyRef}
              className="body"
              value={body}
              onChange={(e) => { setBody(e.target.value); markDirty(); }}
              spellCheck={false}
              placeholder="在這裡撰寫內文（Markdown）…"
            />
          )}
        </div>
      )}

      <footer className="editor-foot">
        <button className="btn primary big" onClick={() => save()} disabled={saving || loading || !dirty}>
          {saving ? "儲存中…" : dirty ? `儲存（${statusLabel}）` : "已是最新"}
        </button>
      </footer>
    </div>
  );
}
